import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { AppMessageTemplateStyle } from '@pushly/models/lib/enums/app-messages/app-message-template-style'
import titleCase from 'title-case'
import { getClassNames } from '../../../_utils/classnames'
import { InfoCircleOutlined } from '@ant-design/icons'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import TemplateMediaField from '../../notification-builder/elements/template-fields/media-field'
import { CtaButtonType } from '../types'
import { Position } from '@pushly/models/lib/enums/position'
import { AppMessageImageSize } from '@pushly/models/lib/enums/app-messages/app-message-image-size'
import 'react-quill/dist/quill.snow.css'
import { AppMessageTemplateImageConfig } from '@pushly/models/lib/structs/app-messages/app-message-template-image-config'
import { AppMessageTemplateButtonConfig } from '@pushly/models/lib/structs/app-messages/app-message-template-button-config'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { getCustomMacroFields } from '../../../_utils/domain'
import { AppMessageCtasBuilder } from './app-message-ctas-builder'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { DomainDto } from '../../../dtos/domain'
import { CustomQuillEditor } from '../../custom-quill-editor/custom-quill-editor'
import { MutableTogglableTextField } from '@pushly/models/lib/structs'
import { EditorActionRef } from '../../custom-quill-editor/types'
import { enumAsSelectOptions } from '../../../_utils/antd'
import { headerPlaceholder, titlePlaceholder } from '../../../features/app-messages/build-app-message/constants'
import { APP_MESSAGE_IMAGE_GUIDANCE, DEFAULT_TOGGLEABLE_FG_COLOR } from '../constants'
import { ConditionalTooltip } from '../../conditional-popover/conditional-popover'
import TemplatizedTextField, {
    InputFocusRef,
} from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import { ColorSwatchField } from '@pushly/aqe/lib/components'
import { Radio, Skeleton } from 'antd'
import { AppMessageStyleSelector } from './app-message-style-selector'
import { AppMessageContentBlock } from './app-message-content-block'

import './styles/app-message-content-builder.scss'

type AppMessageContentBuilderProps = {
    domain: DomainDto
}

/**
 * Every character-limited field here can hold personalization, whose delivered
 * length is unknowable at edit time, so the limit relaxes while a token is
 * present rather than blocking a value that would have fitted.
 */
const PERSONALIZABLE_LIMIT = { maxConstraint: true, relaxMaxOnPersonalization: true } as const

export const AppMessageContentBuilder = forwardRef<EditorActionRef, AppMessageContentBuilderProps>((props, ref) => {
    const { domain } = props

    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context

    const titleRef = useRef<InputFocusRef>(null)
    const headerRef = useRef<InputFocusRef>(null)
    const bodyRef = ref ?? useRef<EditorActionRef>(null)

    // internal working value state ensures clean render of children
    const workingValue = variants[selectedVariantIdx].template!

    const selectedStyle = workingValue.style ?? AppMessageTemplateStyle.MODAL
    const isBanner = selectedStyle === AppMessageTemplateStyle.BANNER

    // dirty fields track original state versus when a user has manually interacted via controlled form field
    // necessary for fields that can be removed from view/form via style options
    const [dirtyFieldsEnabled, setDirtyFieldsEnabled] = useState<{ [key: string]: boolean }>({
        title: workingValue.title.enabled,
        header: workingValue.header.enabled,
        ctaPrimary: workingValue.ctaPrimary.enabled,
        ctaSecondary: workingValue.ctaSecondary.enabled,
    })

    const [customMacros, setCustomMacros] = React.useState()
    // Held in state rather than a ref so the editor re-renders once the slot
    // exists and can portal its toolbar into it.
    const [bodyToolbarSlot, setBodyToolbarSlot] = useState<HTMLElement | null>(null)

    useEffect(() => {
        const getMacros = async () => {
            const customMacroItems = await getCustomMacroFields(props.domain.id)
            setCustomMacros(customMacroItems)
        }

        getMacros()
    }, [])

    // -------------------------- Value Handlers -------------------------- \\
    const onChange = (change: Partial<AppMessageUiTemplate>) => {
        dispatch({ type: 'patch', entity: 'template', data: change })
    }

    // These dispatch a change rather than a rebuilt field, so the reducer merges
    // it against whatever the field holds at that moment. Rebuilding here would
    // read a value captured at render time, and two edits landing before the
    // re-render — two debounced inputs in one tick — would lose the first.
    const handleTemplateImageUpdate = (change: Partial<AppMessageTemplateImageConfig>) => {
        dispatch({ type: 'patch', entity: 'template-field', data: { field: 'image', change } })
    }

    const handleTemplateTextFieldUpdate = (
        change: Partial<MutableTogglableTextField>,
        property: 'title' | 'header' | 'body',
    ) => {
        dispatch({ type: 'patch', entity: 'template-field', data: { field: property, change } })
    }

    const handleCtaFieldUpdate = (change: Partial<AppMessageTemplateButtonConfig>, action: CtaButtonType) => {
        const field = action === CtaButtonType.PRIMARY ? 'ctaPrimary' : 'ctaSecondary'

        dispatch({ type: 'patch', entity: 'template-field', data: { field, change } })
    }

    // These sit in a block header rather than a form body, so they are a label
    // and a control — not Form.Items, whose label column collapses when there is
    // no grid to size against.
    // Position and Fit sit beside the thumbnail rather than in the block header:
    // the image itself is small, and the space to its right is where the reader
    // is already looking.
    const imageProperties = (
        <div className="image-properties">
            <div className="image-property">
                <span className="image-property-label">Position</span>
                <ConditionalTooltip
                    title="Banner app message images are locked to a left-aligned position when used with the primary button."
                    condition={workingValue.image.enabled && isBanner && workingValue.ctaPrimary.enabled}
                >
                    <Radio.Group
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                        className="position-group"
                        disabled={
                            !workingValue.image.enabled ||
                            context.disabled ||
                            (isBanner && workingValue.ctaPrimary.enabled)
                        }
                        onChange={(ev) => {
                            handleTemplateImageUpdate({ position: ev.target.value })
                        }}
                        value={workingValue.image.position ?? Position.TOP}
                    >
                        {enumAsSelectOptions(Position)
                            .filter((opt) => {
                                return isBanner
                                    ? opt.value !== Position.TOP &&
                                          opt.value !== Position.BOTTOM &&
                                          opt.value !== Position.BACKGROUND
                                    : opt.value !== Position.LEFT &&
                                          opt.value !== Position.RIGHT &&
                                          opt.value !== Position.BACKGROUND
                            })
                            .map((p) => (
                                <Radio.Button key={p.value} value={p.value}>
                                    {titleCase(p.label)}
                                </Radio.Button>
                            ))}
                    </Radio.Group>
                </ConditionalTooltip>
            </div>

            <div className="image-property">
                <span className="image-property-label">Fit</span>
                <Radio.Group
                    buttonStyle="solid"
                    optionType="button"
                    size="small"
                    disabled={!workingValue.image.enabled || context.disabled}
                    onChange={(ev) => {
                        handleTemplateImageUpdate({ size: ev.target.value })
                    }}
                    value={workingValue.image.size ?? AppMessageImageSize.COVER}
                >
                    {Object.keys(AppMessageImageSize).map((size) => (
                        <Radio.Button key={size} value={AppMessageImageSize[size]}>
                            {titleCase(size)}
                        </Radio.Button>
                    ))}
                </Radio.Group>
                <span className="image-property-hint">
                    {(workingValue.image.size ?? AppMessageImageSize.COVER) === AppMessageImageSize.COVER
                        ? 'Fills the frame, edges cropped'
                        : 'Fits inside the frame, whole image shown'}
                </span>
            </div>

            <p className="image-requirements">
                {APP_MESSAGE_IMAGE_GUIDANCE[selectedStyle] ?? APP_MESSAGE_IMAGE_GUIDANCE[AppMessageTemplateStyle.MODAL]}
            </p>
        </div>
    )

    return (
        <Well step={2} title="Message" className={getClassNames('app-message-content-builder')} footerMode="hidden">
            <Skeleton loading={context.loading} active={true} avatar={false} title={false}>
                <Well.Section title="Style">
                    <AppMessageStyleSelector
                        value={workingValue}
                        onChange={onChange}
                        dirtyFieldsEnabled={dirtyFieldsEnabled}
                        disabled={context.disabled}
                    />
                </Well.Section>

                <Well.Section title="Content" className={getClassNames('app-message-content-blocks')}>
                    <AppMessageContentBlock
                        label="Image"
                        info="This image will be displayed in the specified position and fit selected within the boundaries of the app message."
                        enabled={workingValue.image.enabled}
                        disabled={context.disabled}
                        className="content-block-image"
                        onEnabledChange={(enabled) => {
                            const image = workingValue.image.toMutable()
                            image.enabled = enabled

                            // set default properties via form control
                            image.position = isBanner ? Position.LEFT : Position.TOP
                            image.size = AppMessageImageSize.COVER

                            onChange({ image })
                        }}
                    >
                        <div className="image-layout">
                            <TemplateMediaField
                                key={workingValue.image.enabled ? 'enabled' : 'disabled'}
                                disabled={context.disabled}
                                className="image-upload"
                                domainId={domain.id}
                                defaultDomainMediaUrl={domain.defaultIconUrl ?? undefined}
                                accountId={domain.accountId}
                                value={workingValue.image.source}
                                onChange={(v) => {
                                    handleTemplateImageUpdate({ source: v ?? undefined })
                                }}
                                imageType="app-message-image"
                                pjpg={true}
                                forceCrop={isBanner}
                                cropRatio={isBanner ? 1 : null}
                                size="large"
                                hideMacroToggle={true}
                            />

                            {imageProperties}
                        </div>
                    </AppMessageContentBlock>

                    {!isBanner && (
                        <>
                            <AppMessageContentBlock
                                label="Header"
                                info="A short line above the title, for context such as a category or an offer name."
                                enabled={workingValue.header.enabled}
                                disabled={context.disabled}
                                className="content-block-header"
                                toolbar={
                                    <ColorSwatchField
                                        aria-label="Header text color"
                                        disabled={context.disabled}
                                        value={workingValue.header.fgColor ?? DEFAULT_TOGGLEABLE_FG_COLOR}
                                        onChange={(color) =>
                                            handleTemplateTextFieldUpdate({ fgColor: color }, 'header')
                                        }
                                    />
                                }
                                onEnabledChange={(enabled) => {
                                    handleTemplateTextFieldUpdate({ enabled }, 'header')
                                    setDirtyFieldsEnabled((currentState) => ({ ...currentState, header: enabled }))
                                    if (headerRef.current) {
                                        headerRef.current.focusInput()
                                    }
                                }}
                            >
                                <TemplatizedTextField
                                    ref={headerRef}
                                    aria-label="Header text"
                                    className="app-message-header"
                                    disabled={context.disabled}
                                    placeholder={headerPlaceholder}
                                    limits={{ ...PERSONALIZABLE_LIMIT, max: 55 }}
                                    customMacroOptions={customMacros}
                                    value={workingValue.header.value}
                                    onChange={(field) => {
                                        handleTemplateTextFieldUpdate({ value: field }, 'header')
                                    }}
                                />
                            </AppMessageContentBlock>

                            <AppMessageContentBlock
                                label="Title"
                                info="The message's headline. Keep it short enough to read at a glance."
                                enabled={workingValue.title.enabled}
                                disabled={context.disabled}
                                className="content-block-title"
                                toolbar={
                                    <ColorSwatchField
                                        aria-label="Title text color"
                                        disabled={context.disabled}
                                        value={workingValue.title.fgColor ?? DEFAULT_TOGGLEABLE_FG_COLOR}
                                        onChange={(color) => handleTemplateTextFieldUpdate({ fgColor: color }, 'title')}
                                    />
                                }
                                onEnabledChange={(enabled) => {
                                    handleTemplateTextFieldUpdate({ enabled }, 'title')
                                    setDirtyFieldsEnabled((currentState) => ({ ...currentState, title: enabled }))
                                    if (titleRef.current) {
                                        titleRef.current.focusInput()
                                    }
                                }}
                            >
                                <TemplatizedTextField
                                    ref={titleRef}
                                    aria-label="Title text"
                                    className="app-message-title"
                                    disabled={context.disabled}
                                    placeholder={titlePlaceholder}
                                    limits={{ ...PERSONALIZABLE_LIMIT, max: 65 }}
                                    customMacroOptions={customMacros}
                                    value={workingValue.title.value}
                                    onChange={(field) => {
                                        handleTemplateTextFieldUpdate({ value: field }, 'title')
                                    }}
                                />
                            </AppMessageContentBlock>
                        </>
                    )}

                    <AppMessageContentBlock
                        label="Body"
                        info="The main text of the message. Formatting, emoji and personalization are available from the toolbar."
                        enabled={workingValue.body.enabled}
                        disabled={context.disabled}
                        className="content-block-body-field app-message-body"
                        toolbar={<span className="body-toolbar-slot" ref={setBodyToolbarSlot} />}
                        onEnabledChange={(enabled) => {
                            handleTemplateTextFieldUpdate({ enabled }, 'body')
                            if (ref && (ref as any).current) {
                                ;(ref as any).current.focusEditor?.()
                            }
                        }}
                    >
                        {/* Quill resolves its toolbar container when it
                            initialises, so the editor must not mount until the
                            header slot it portals into exists. */}
                        {!!bodyToolbarSlot && (
                            <CustomQuillEditor
                                ref={bodyRef}
                                key={context.selectedTemplateId ?? 'q-editor'}
                                value={workingValue.body.value}
                                disabled={!workingValue.body.enabled || context.disabled}
                                onChange={(change) => handleTemplateTextFieldUpdate({ value: change }, 'body')}
                                formats={[
                                    'bold',
                                    'italic',
                                    'strike',
                                    'list',
                                    'bullet',
                                    'indent',
                                    'align',
                                    'direction',
                                    'color',
                                    'emojis',
                                    'macros',
                                ]}
                                macros={customMacros}
                                toolbarContainer={bodyToolbarSlot}
                                contentEditableProps={{
                                    backgroundColor: workingValue.bgColor,
                                }}
                            />
                        )}
                    </AppMessageContentBlock>
                </Well.Section>

                <Well.Section className={getClassNames('app-message-buttons')}>
                    <AppMessageCtasBuilder
                        value={workingValue}
                        onChange={(change, action) => {
                            handleCtaFieldUpdate(change, action)

                            const field = action === 'PRIMARY' ? 'ctaPrimary' : 'ctaSecondary'
                            if ('enabled' in change) {
                                setDirtyFieldsEnabled((current) => ({
                                    ...current,
                                    [field]: change.enabled!,
                                }))
                            }
                        }}
                        onTemplateChange={onChange}
                        disabled={context.disabled}
                    />
                </Well.Section>
            </Skeleton>
        </Well>
    )
})
